import type React from "react";
import { useEffect, useState, useRef } from "react";
import type { MusicSelectorProps, MusicType } from "./types";
import { Label } from "../../misc/Label";
import { Button } from "../../misc/Button";
import {
    FaMusic,
    FaPause,
    FaPlay,
    FaChevronDown,
    FaChevronUp,
} from "react-icons/fa";
import { Switch } from "../../misc/Switch";
import { getAuthHeader } from "../../../utils/authHeader";
import axios from "axios";
import CustomToast from "../../misc/CustomToast";
import { toast } from "react-toastify";

const Music: React.FC<MusicSelectorProps> = ({
    includeMusic,
    setIncludeMusic,
    setLoading,
    music,
    setMusic,
    selectedMusic,
    setSelectedMusic,
}) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [playingTrack, setPlayingTrack] = useState<string | null>(null);
    const [showMusicSelector, setShowMusicSelector] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    // Latest selectedMusic in a ref so the fetch effect can read it for the
    // default-selection without depending on it (the fetch must run once on
    // mount, not on every track change).
    const selectedMusicRef = useRef(selectedMusic);
    useEffect(() => {
        selectedMusicRef.current = selectedMusic;
    }, [selectedMusic]);

    const toggleMusicSelector = (): void => {
        setShowMusicSelector((prev) => !prev);
    };

    const playTrack = (trackUrl: string): void => {
        if (!audioRef.current) return;

        // If already playing this track, pause it
        if (isPlaying && playingTrack === trackUrl) {
            audioRef.current.pause();
            setIsPlaying(false);
            setPlayingTrack(null);
            return;
        }

        // If playing a different track, switch to the new one
        audioRef.current.src = trackUrl;
        audioRef.current.play().catch(() => {
            toast.error(
                <CustomToast
                    message="Failed to play the music."
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
        });

        setIsPlaying(true);
        setPlayingTrack(trackUrl);
    };

    const selectTrack = (trackName: string): void => {
        setSelectedMusic(trackName);
    };

    // Get the currently selected track object
    const selectedTrackObj = music?.find(
        (track) => track.name === selectedMusic
    );

    useEffect(() => {
        const fetchMusic = async (): Promise<void> => {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            try {
                const response = await axios.get(
                    `${backendUrl}/listing/slideshow/music`,
                    {
                        headers,
                    }
                );

                if (response.status === 200) {
                    const musicUrl: MusicType[] = response.data;
                    setMusic(musicUrl);
                    if (musicUrl && musicUrl.length > 0) {
                        // Set the first track as selected by default if none is selected
                        if (!selectedMusicRef.current) {
                            setSelectedMusic(musicUrl[0].name);
                        }

                        // Initialize audio element
                        if (!audioRef.current) {
                            audioRef.current = new Audio();
                            audioRef.current.loop = true;
                            audioRef.current.addEventListener("ended", () => {
                                setIsPlaying(false);
                                setPlayingTrack(null);
                            });
                        }
                    }
                } else {
                    toast.error(
                        <CustomToast
                            message="An error occurred while fetching music."
                            type="error"
                        />,
                        {
                            autoClose: 3000,
                        }
                    );
                }
            } catch (error: unknown) {
                setLoading(false);
                if (axios.isAxiosError(error)) {
                    if (error.response?.status !== 404) {
                        toast.error(
                            <CustomToast
                                message="An error occurred while fetching music."
                                type="error"
                            />,
                            {
                                autoClose: 3000,
                            }
                        );
                    }
                } else {
                    toast.error(
                        <CustomToast
                            message="An unexpected error occurred."
                            type="error"
                        />,
                        {
                            autoClose: 3000,
                        }
                    );
                }
            } finally {
                setLoading(false);
            }
        };
        fetchMusic();
        // Runs once on mount (the setters are stable); selectedMusic is read via
        // a ref so picking a track doesn't refetch the whole list.
    }, [setLoading, setMusic, setSelectedMusic]);

    // Cleanup the audio on unmount
    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    return (
        <div className="space-y-4">
            <div className="flex items-center space-x-4">
                <Switch
                    id="music"
                    checked={includeMusic}
                    onCheckedChange={(checked) => {
                        setIncludeMusic(checked);
                        if (!checked && isPlaying) {
                            if (audioRef.current) {
                                audioRef.current.pause();
                                setIsPlaying(false);
                                setPlayingTrack(null);
                            }
                        }
                    }}
                />
                <Label htmlFor="music">
                    Include Background Music - Add music to the Slideshow
                </Label>
            </div>

            {includeMusic && music && music.length > 0 && (
                <div className="pl-8">
                    <Button
                        variant="outline"
                        className="flex items-center justify-between w-full min-[768px]:w-64 mb-2"
                        onClick={toggleMusicSelector}
                    >
                        <div className="flex items-center gap-2">
                            <FaMusic className="h-4 w-4 text-primary" />
                            <span>
                                {selectedTrackObj?.name?.replace(
                                    /\.mp3$/i,
                                    ""
                                ) || "Select music"}
                            </span>
                        </div>
                        {showMusicSelector ? (
                            <FaChevronUp className="h-4 w-4 text-secondary" />
                        ) : (
                            <FaChevronDown className="h-4 w-4 text-secondary" />
                        )}
                    </Button>

                    {showMusicSelector && (
                        <div className="border rounded-md p-2 bg-white shadow-sm max-h-60 overflow-y-auto w-full min-[768px]:w-64">
                            {music.map((track) => (
                                <div
                                    key={track.name}
                                    className={`flex items-center justify-between p-2 rounded-md cursor-pointer hover:bg-gray-100 ${
                                        selectedMusic === track.name
                                            ? "bg-gray-100"
                                            : ""
                                    }`}
                                    onClick={() => selectTrack(track.name)}
                                >
                                    <div className="flex items-center gap-3">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 rounded-full p-0"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                playTrack(track.url);
                                            }}
                                        >
                                            {isPlaying &&
                                            playingTrack === track.url ? (
                                                <FaPause className="h-3 w-3 text-secondary" />
                                            ) : (
                                                <FaPlay className="h-3 w-3 text-secondary" />
                                            )}
                                        </Button>

                                        <div className="text-sm">
                                            {track.name.replace(/\.mp3$/i, "")}
                                        </div>
                                    </div>

                                    <div
                                        className={`w-3 h-3 rounded-full ${
                                            selectedMusic === track.name
                                                ? "bg-primary"
                                                : "border border-gray-300"
                                        }`}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Music;
